import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { CallbackErrorAxios, CallbackWarningAxios, Client, CommonGlobalService, Option, RootState, Sector, Site, UUID } from '@umanforge/common';
import { ClientService } from 'services/client.service';
import { SiteService } from 'services/site.service';
import { SectorService } from 'services/sector.service';
import { Dispatch } from '@reduxjs/toolkit';

export type ExportFilters = {
  filterSites?: UUID[];
  filterSectors?: UUID[];
};

export function useExport(
  isOpen: boolean,
  exportFunction: ( client: Client, token: string | null, dispatch: Dispatch, onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios, filters?: ExportFilters ) => Promise<boolean>,
  onClose: () => void,
  showSiteFilter: boolean = false,
  showSectorFilter: boolean = false
) {

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );

  const [ selectedClients, setSelectedClients ] = useState<Option<Client>[]>( [] );
  const [ selectedSites, setSelectedSites ] = useState<Option<Site>[]>( [] );
  const [ selectedSectors, setSelectedSectors ] = useState<Option<Sector>[]>( [] );

  const [ allowedClients, setAllowedClients ] = useState<Option<Client>[]>( [] );
  const [ allowedSites, setAllowedSites ] = useState<Option<Site>[]>( [] );
  const [ allowedSectors, setAllowedSectors ] = useState<Option<Sector>[]>( [] );

  const [ canExport, setCanExport ] = useState<boolean>( false );
  const [ displayModal, setDisplayModal ] = useState<boolean>( false );
  const [ isLoading, setIsLoading ] = useState<boolean>( true );

  useEffect( () => {
    init();
  }, [ token ] );

  useEffect( () => {
    setDisplayModal( isOpen );
    if ( isOpen && allowedClients.length === 1 && selectedClients.length === 0 ) {
      setSelectedClients( allowedClients );
    }
  }, [ isOpen, allowedClients, selectedClients ] );

  useEffect( () => {
    const clientIds = selectedClients.map( client => { return client.object.id; } );

    if ( !showSiteFilter && !showSectorFilter ) {
      setAllowedSites( [] );
      setSelectedSites( [] );
      setAllowedSectors( [] );
      setSelectedSectors( [] );
      return;
    }

    if ( clientIds.length === 0 ) {
      setAllowedSites( [] );
      setSelectedSites( [] );
      setAllowedSectors( [] );
      setSelectedSectors( [] );
      return;
    }

    const refreshLinkedFilters = async () => {
      const [ responseSites, responseSectors ] = await Promise.all( [
        SiteService.getAllowedSites( null, token, dispatch ) ?? [],
        SectorService.getAllowedSectors( null, token, dispatch ) ?? []
      ] );

      const availableSites = responseSites
        .filter( site => { return clientIds.includes( site.clientId ); } );
      const siteOptions = CommonGlobalService.toOptions( availableSites )
        .sort( ( site1, site2 ) => { return site1.label < site2.label ? -1 : 1; } );

      const availableSectors = responseSectors
        .filter( sector => { return siteOptions.some( site => { return site.value === sector.siteId; } ); } );
      const sectorOptions = CommonGlobalService.toOptions( availableSectors )
        .sort( ( sector1, sector2 ) => { return sector1.label < sector2.label ? -1 : 1; } );

      setAllowedSites( showSiteFilter ? siteOptions : [] );
      setSelectedSites( showSiteFilter ? selectedSites.filter( site => { return siteOptions.some( option => { return option.value === site.value; } ); } ) : [] );
      setAllowedSectors( showSectorFilter ? sectorOptions : [] );
      setSelectedSectors( showSectorFilter ? selectedSectors.filter( sector => { return sectorOptions.some( option => { return option.value === sector.value; } ); } ) : [] );
    };

    refreshLinkedFilters();
  }, [ selectedClients, token, dispatch, showSiteFilter, showSectorFilter ] );

  useEffect( () => {
    setCanExport( selectedClients.length > 0 );
  }, [ selectedClients ] );

  const init = async () => {
    await refreshClients();
  };

  const refreshClients = async () => {
    setIsLoading( true );
    const response = await ClientService.getAllowedClients( null, token, dispatch );
    const clients = CommonGlobalService.toOptions( response )
      .sort( ( client1, client2 ) => { return client1.label < client2.label ? -1 : 1; } );
    setAllowedClients( clients );
    setIsLoading( false );
  };

  const tryExportData = useCallback( async ( client: Client, filters?: ExportFilters ) => {
    await exportFunction( client, token, dispatch, undefined, undefined, filters );
  }, [ token, dispatch, exportFunction ] );

  return {
    displayModal,
    canExport,
    isLoading,
    setIsLoading,
    allowedClients,
    selectedClients,
    setSelectedClients,
    allowedSites,
    selectedSites,
    setSelectedSites,
    allowedSectors,
    selectedSectors,
    setSelectedSectors,
    tryExportData,
  };
}